import React, { useState } from "react";
import { Button, Form } from "react-bootstrap";
import HelpCard from "components/Cards/HelpCard";
import NoteCard from "components/Cards/NoteCard";
import Validator from "utils/Validator";
import { get_seo_article_headline_for_single_keyword, submitKeywordLevelContentOutlineRequest, submitContentOutlineDraftRequest, submitAiOverviewByContentRequest } from "services/buddyXApiHubService";
import InsufficientCreditsPopup from "components/InsufficientCreditsPopup";
import ToastAlert from "components/Alert/ToastAlert";
import ArticleHeadlinesKeywordTable from "components/Tables/ArticleHeadlinesKeywordTable";
import MarkdownRender from "components/MarkdownRender/textBox";
import AiOverviewTable from "components/MarkdownRender/tables/AiOverviewTable";
import TopTabBar from "components/TabBars/TopTabBar";
import classNames from "classnames";
import { css } from "aphrodite";
import pageStyles from "styles/pageStyles";

export default function Summary1() {
  // Color variables for consistent theming
  const stepColors = {
    active: '#007bff',      // Blue for active/incomplete steps
    completed: '#28a745',   // Green for completed steps
    background: '#f8f9fa',  // Light gray background
    shadow: '0 1px 3px rgba(0,0,0,0.1)' // Subtle shadow
  };

  // Helper function to get input field styles
  const getInputFieldStyle = (isCompleted) => ({
    backgroundColor: stepColors.background,
    border: `2px solid ${isCompleted ? stepColors.completed : stepColors.active}`,
    borderRadius: '8px',
    boxShadow: stepColors.shadow
  });

  // State to track completion of each step
  const [completedSteps, setCompletedSteps] = useState({
    step1: false, // Headline Creation
    step2: false, // Content Outline  
    step3: false, // Content Draft
    step4: false  // AI Overview
  });

  // Remove currentStep state - we'll show all steps simultaneously

  // Form states for each step
  const [step1Data, setStep1Data] = useState({
    keyword: "",
    brandName: "",
    headlines: null,
    isLoading: false,
    errors: { keyword: "", brandName: "" }
  });

  const [step2Data, setStep2Data] = useState({
    title: "",
    primaryKeyword: "",
    brand: "",
    model: "",
    wordCount: 800,
    outline: "",
    isLoading: false,
    errors: { title: "", primaryKeyword: "", brand: "", model: "", wordCount: "" }
  });

  const [step3Data, setStep3Data] = useState({
    draft: "",
    isLoading: false,
    loadingModel: null, // Track which model is currently loading: "writer_1" or "writer_2"
    errors: {}
  });

  const [step4Data, setStep4Data] = useState({
    text: "",
    noQueries: 10,
    aiOverview: [],
    isLoading: false,
    errors: { noQueries: "" }
  });

  // Notification states
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [insufficientCreditsData, setInsufficientCreditsData] = useState(null);
  const [showInsufficientCredits, setShowInsufficientCredits] = useState(false);
  
  // Copy functionality states
  const [copiedContent, setCopiedContent] = useState(null);
  
  // Global loading state - true when ANY step is generating content
  const [isAnyStepLoading, setIsAnyStepLoading] = useState(false);

  // Timeline data
  const timelineData = [
    {
      id: 1,
      duration: "45seconds",
      activity: "Article Headline",
      isSpecial: false
    },
    {
      id: 2,
      duration: "2-3minutes",
      activity: "Content Outline",
      isSpecial: false
    },
    {
      id: 3,
      duration: "3-4minutes",
      activity: "Content Draft",
      isSpecial: false
    },
    {
      id: 4,
      duration: "2minutes",
      activity: "AI Overview",
      isSpecial: false
    },
    {
      id: 5,
      duration: "7-8minutes",
      activity: "Total Time",
      isSpecial: true
    }
  ];

  // Model options for writer
  const modelOptions = [
    { value: "writer_1", label: "Writer 1" },
    { value: "writer_2", label: "Writer 2" },
    { value: "writer_3", label: "Writer 3" },
    { value: "writer_4", label: "Writer 4" }
  ];

  // Model action mapping for draft generation
  const model_action_map = {
    "writer_1": "keyword_level_content_draft",
    "writer_2": "keyword_level_content_draft_advanced"
  };

  // Note: Progress is no longer saved to localStorage - resets on page refresh

  const roadmapSteps = [
    {
      id: 1,
      title: "Headline",
      subtitle: "Create compelling headlines",
      isCompleted: completedSteps.step1,
      isActive: true, // Always available as starting point
      link: "/content_editor/headline/headline_creation",
      description: "Generate engaging headlines that capture attention and improve click-through rates."
    },
    {
      id: 2,
      title: "Structure",
      subtitle: "Build content outline",
      isCompleted: completedSteps.step2,
      isActive: completedSteps.step1, // Unlocked after step 1
      link: "/content_editor/writer/keyword",
      description: "Create a well-structured content outline based on your target keywords."
    },
    {
      id: 3,
      title: "Create",
      subtitle: "Write content draft",
      isCompleted: completedSteps.step3,
      isActive: completedSteps.step2, // Unlocked after step 2
      link: "/content_editor/writer/keyword",
      description: "Generate high-quality content drafts using your outline and keywords."
    },
    {
      id: 4,
      title: "Optimize",
      subtitle: "AI overview analysis",
      isCompleted: completedSteps.step4,
      isActive: completedSteps.step3, // Unlocked after step 3
      link: "/content_editor/ai_overview/content",
      description: "Get AI-powered insights and optimization suggestions for your content."
    },
    {
      id: 5,
      title: "Publish",
      subtitle: "Go live with confidence",
      isCompleted: false,
      isActive: completedSteps.step4, // Unlocked after step 4
      link: "#",
      description: "Your content is ready to publish and drive results!"
    }
  ];

  // Calculate current progress
  const completedCount = Object.values(completedSteps).filter(Boolean).length;
  const progressPercentage = (completedCount / 4) * 100; // 4 actual steps (excluding publish)

  // Page structure variables (similar to other content editor pages)
  const firstLevelTopic = "Content Editor";
  const secondLevelTopic = "Content Journey";
  const thirdLevelTopic = "Keyword";

  const getStepStyles = (step) => {
    if (step.isCompleted) {
      return {
        background: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)',
        color: 'white',
        border: '2px solid #28a745'
      };
    } else if (step.isActive) {
      return {
        background: 'linear-gradient(135deg, #4A90E2 0%, #357ABD 100%)',
        color: 'white',
        transform: 'scale(1.05)',
        boxShadow: '0 8px 25px rgba(74, 144, 226, 0.3)',
        cursor: 'pointer'
      };
    } else {
      return {
        background: '#f8f9fa',
        color: '#6c757d',
        border: '2px solid #e9ecef',
        opacity: 0.6,
        cursor: 'not-allowed'
      };
    }
  };

  // Handler functions for insufficient credits
  const handleInsufficientCredits = (balanceData) => {
    setInsufficientCreditsData(balanceData);
    setShowInsufficientCredits(true);
  };

  const closeInsufficientCredits = () => {
    setShowInsufficientCredits(false);
  };

  // Step 1: Headline Creation handlers
  const handleStep1InputChange = (e) => {
    const { name, value } = e.target;
    setStep1Data(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleStep1Blur = (e) => {
    const { name, value } = e.target;
    let error = "";
    if (name === "keyword" && !Validator.validateRequired(value, Validator.validateSingleKeyword)) {
      error = "Please enter keywords";
    }
    setStep1Data(prev => ({
      ...prev,
      errors: { ...prev.errors, [name]: error }
    }));
  };

  const handleStep1Submit = async () => {
    if (!step1Data.keyword.trim()) return;
    
    setStep1Data(prev => ({ ...prev, isLoading: true }));
    setIsAnyStepLoading(true);
    
    try {
      const formData = {
        keywords: [step1Data.keyword],
        brand_name: step1Data.brandName
      };
      
      const result = await get_seo_article_headline_for_single_keyword(formData, handleInsufficientCredits);
      
      setStep1Data(prev => ({
        ...prev,
        headlines: result,
        isLoading: false
      }));
      setIsAnyStepLoading(false);
      
      setCompletedSteps(prev => ({ ...prev, step1: true }));
      setShowSuccessNotification(true);
      
      // Auto-populate step 2 with selected headline and keyword
      const firstResultKey = Object.keys(result)[0];
      if (firstResultKey && result[firstResultKey] && result[firstResultKey].length > 0) {
        setStep2Data(prev => ({
          ...prev,
          title: result[firstResultKey][0].headline || "",
          primaryKeyword: step1Data.keyword,
          brand: step1Data.brandName
        }));
      }
      
    } catch (error) {
      setErrorMessage(error.message || "Failed to generate headlines");
      setShowErrorNotification(true);
      setStep1Data(prev => ({ ...prev, isLoading: false }));
      setIsAnyStepLoading(false);
    }
  };

  // Step 2: Content Outline handlers
  const handleStep2InputChange = (e) => {
    const { name, value } = e.target;
    setStep2Data(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleStep2Blur = (e) => {
    const { name, value } = e.target;
    let error = "";
    
    if (name === "title" && !value.trim()) {
      error = "Please enter article headline";
    } else if (name === "primaryKeyword" && !value.trim()) {
      error = "Please enter primary keyword";
    } else if (name === "brand" && !value.trim()) {
      error = "Please enter brand name";
    } else if (name === "model" && !value) {
      error = "Please select writer model";
    } else if (name === "wordCount") {
      const num = parseInt(value);
      if (!num || num < 100 || num > 1200) {
        error = "Word count must be between 100 and 1200";
      }
    }
    
    setStep2Data(prev => ({
      ...prev,
      errors: { ...prev.errors, [name]: error }
    }));
  };

  const handleStep2Submit = async () => {
    if (!step2Data.title.trim() || !step2Data.primaryKeyword.trim() || !step2Data.brand.trim() || !step2Data.model) return;
    
    setStep2Data(prev => ({ ...prev, isLoading: true }));
    setIsAnyStepLoading(true);
    
    try {
      const formData = {
        title: step2Data.title,
        primary_keyword: step2Data.primaryKeyword,
        brand: step2Data.brand,
        model: step2Data.model,
        word_count: step2Data.wordCount
      };
      
      const result = await submitKeywordLevelContentOutlineRequest(formData, "keyword_level_content_outliner", handleInsufficientCredits);
      
      setStep2Data(prev => ({
        ...prev,
        outline: result,
        isLoading: false
      }));
      setIsAnyStepLoading(false);
      
      setCompletedSteps(prev => ({ ...prev, step2: true }));
      setShowSuccessNotification(true);
      
      // Store the original keyword for draft generation
      setStep3Data(prev => ({
        ...prev,
        originalKeyword: step2Data.primaryKeyword,
        outline: result
      }));
      
    } catch (error) {
      setErrorMessage(error.message || "Failed to generate content outline");
      setShowErrorNotification(true);
      setStep2Data(prev => ({ ...prev, isLoading: false }));
      setIsAnyStepLoading(false);
    }
  };

  // Step 3: Content Draft handlers
  const handleStep3Submit = async (selectedModel = "writer_1") => {
    if (!step2Data.outline) return;
    
    setStep3Data(prev => ({ 
      ...prev, 
      isLoading: true,
      loadingModel: selectedModel 
    }));
    setIsAnyStepLoading(true);
    
    try {
      // Prepare data for draft generation using the original keyword
      const draftData = {
        seo_outline: step2Data.outline,
        primary_keyword: step2Data.primaryKeyword,
        model: selectedModel
      };
      
      const result = await submitContentOutlineDraftRequest(draftData, model_action_map[selectedModel], handleInsufficientCredits);
      
      setStep3Data(prev => ({
        ...prev,
        draft: result,
        isLoading: false,
        loadingModel: null
      }));
      setIsAnyStepLoading(false);
      
      setCompletedSteps(prev => ({ ...prev, step3: true }));
      setShowSuccessNotification(true);
      
      // Auto-populate step 4 with draft content
      setStep4Data(prev => ({
        ...prev,
        text: result
      }));
      
    } catch (error) {
      setErrorMessage(error.message || "Failed to generate content draft");
      setShowErrorNotification(true);
      setStep3Data(prev => ({ 
        ...prev, 
        isLoading: false,
        loadingModel: null 
      }));
      setIsAnyStepLoading(false);
    }
  };

  // Step 4: AI Overview handlers
  const handleStep4InputChange = (e) => {
    const { name, value } = e.target;
    setStep4Data(prev => ({
      ...prev,
      [name]: name === "noQueries" ? parseInt(value) || 1 : value
    }));
  };

  const handleStep4Blur = (e) => {
    const { name, value } = e.target;
    let error = "";
    
    if (name === "noQueries") {
      const num = parseInt(value);
      if (!num || num < 1 || num > 50) {
        error = "Number of queries must be between 1 and 50";
      }
    }
    
    setStep4Data(prev => ({
      ...prev,
      errors: { ...prev.errors, [name]: error }
    }));
  };

  const handleStep4Submit = async () => {
    if (!step4Data.text.trim()) return;
    
    setStep4Data(prev => ({ ...prev, isLoading: true }));
    setIsAnyStepLoading(true);
    
    try {
      // Prepare data for AI overview generation using the generated draft
      const aiOverviewData = {
        text: step4Data.text,
        no_queries: step4Data.noQueries
      };
      
      const result = await submitAiOverviewByContentRequest(aiOverviewData, "ai_overview_content", handleInsufficientCredits);
      
      setStep4Data(prev => ({
        ...prev,
        aiOverview: result,
        isLoading: false
      }));
      setIsAnyStepLoading(false);
      
      setCompletedSteps(prev => ({ ...prev, step4: true }));
      setShowSuccessNotification(true);
      
    } catch (error) {
      setErrorMessage(error.message || "Failed to generate AI overview");
      setShowErrorNotification(true);
      setStep4Data(prev => ({ ...prev, isLoading: false }));
      setIsAnyStepLoading(false);
    }
  };


  const openInNewTab = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // Copy content function
  const handleCopyContent = async (content, contentType) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedContent(contentType);
      setShowSuccessNotification(true);
      
      // Reset copied state after 2 seconds
      setTimeout(() => {
        setCopiedContent(null);
      }, 2000);
    } catch (err) {
      console.error("Failed to copy content: ", err);
      setErrorMessage("Failed to copy content to clipboard");
      setShowErrorNotification(true);
    }
  };

  const getConnectorStyles = (index) => {
    if (index < roadmapSteps.length - 1) {
      const currentStep = roadmapSteps[index];
      
      if (currentStep.isCompleted) {
        return {
          background: 'linear-gradient(90deg, #28a745 0%, #20c997 100%)',
          height: '4px'
        };
      } else if (currentStep.isActive) {
        return {
          background: 'linear-gradient(90deg, #4A90E2 0%, #6c757d 100%)',
          height: '3px'
        };
      } else {
        return {
          background: '#e9ecef',
          height: '2px'
        };
      }
    }
    return {};
  };

  return (
    <div className={classNames("container-fluid", css(pageStyles.container))}>
      <h1 className="d-flex fw-normal justify-content-start align-items-center fw-bold">
        {secondLevelTopic}{"-"}{thirdLevelTopic}
      </h1>
      <p className="fw-normal justify-content-start align-items-center">
        {firstLevelTopic} / <b>{secondLevelTopic}</b> / <b>{thirdLevelTopic}</b>
      </p>
      <p className="fw-normal justify-content-start align-items-center">
        Follow your <b>content creation roadmap</b> to create high-quality, SEO-optimized content. 
        Each step builds upon the previous one, ensuring a systematic approach to content development 
        that drives results and engages your audience.
      </p>
      
      <div className="row">
        <div className="col-12">
          <HelpCard
            topic={"How To Use"}
            description={
              "Content Roadmap guides you through the complete SEO content creation process. Start with headline creation, move to content outline, generate your draft, and finish with AI optimization. Each step unlocks after completing the previous one, ensuring a structured workflow for maximum content effectiveness."
            }
            image={"/icon/rocket.svg"}
          />
        </div>
      </div>
        
        {/* Timeline Section */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="p-3 p-md-4 rounded" style={{ backgroundColor: '#f8f9fa', border: '1px solid #e9ecef' }}>
              <h5 className="fw-bold mb-3 text-center" style={{ color: '#2c3e50' }}>
                Content Creation Timeline
              </h5>
              <p className="text-muted text-center mb-4 small">
                Estimated time breakdown for your content creation journey
              </p>
              
              {/* Desktop Timeline */}
              <div className="d-none d-md-flex justify-content-between align-items-center">
                {timelineData.map((item) => (
                  <div key={item.id} className="text-center" style={{ flex: '1', margin: '0 8px' }}>
                    <div 
                      className="card border-0 shadow-sm" 
                      style={{ 
                        backgroundColor: item.isSpecial ? 'transparent' : 'white', 
                        borderRadius: '12px', 
                        padding: '20px',
                        ...(item.isSpecial && {
                          background: 'linear-gradient(135deg, #4A90E2 0%, #6C5CE7 100%)',
                          color: 'white'
                        })
                      }}
                    >
                      <div className={`fw-bold h4 mb-2 ${item.isSpecial ? 'text-white' : ''}`} style={{ color: item.isSpecial ? 'white' : '#2c3e50' }}>
                        {item.duration}
                      </div>
                      <div className={`small ${item.isSpecial ? 'text-white' : 'text-muted'}`} style={{ 
                        color: item.isSpecial ? 'white' : '#6c757d',
                        opacity: item.isSpecial ? 0.9 : 1
                      }}>
                        {item.activity}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Mobile Timeline */}
              <div className="d-md-none">
                <div className="row g-3">
                  {timelineData.map((item) => (
                    <div key={item.id} className="col-6">
                      <div 
                        className="card border-0 shadow-sm h-100" 
                        style={{ 
                          backgroundColor: item.isSpecial ? 'transparent' : 'white', 
                          borderRadius: '12px', 
                          padding: '15px',
                          ...(item.isSpecial && {
                            background: 'linear-gradient(135deg, #4A90E2 0%, #6C5CE7 100%)',
                            color: 'white'
                          })
                        }}
                      >
                        <div className="text-center">
                          <div className={`fw-bold h5 mb-2 ${item.isSpecial ? 'text-white' : ''}`} style={{ color: item.isSpecial ? 'white' : '#2c3e50' }}>
                            {item.duration}
                          </div>
                          <div className={`small ${item.isSpecial ? 'text-white' : 'text-muted'}`} style={{ 
                            color: item.isSpecial ? 'white' : '#6c757d',
                            opacity: item.isSpecial ? 0.9 : 1
                          }}>
                            {item.activity}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="p-2 mb-2 rounded">
        <div className="d-flex fw-bold mb-0">SEO Content Creation Roadmap</div>
        <div className="row mb-2 align-items-end">
          <div className="mb-2">
            <p className="fw-normal justify-content-start align-items-center">
              Follow these steps to create optimized content. Each step opens in a new tab and unlocks the next step.
            </p>

            {/* Visual Roadmap Representation */}
            <div className="mb-4 p-2 p-sm-3 p-md-4 rounded" style={{ backgroundColor: '#f8f9fa', border: '2px solid #20c997', maxWidth: '100%', overflowX: 'auto' }}>
              <div className="text-center mb-3 mb-md-4">
                <h4 className="fw-bold h6 h-sm-5 h-md-4 mb-2 mb-md-3" style={{ color: '#4A90E2' }}>
                  What is Next? SEO Content Creation Roadmap
                </h4>
                <div className="d-inline-flex align-items-center px-2 px-sm-3 py-1 py-sm-2 rounded-pill" 
                     style={{ 
                       background: 'linear-gradient(135deg, #6C5CE7 0%, #A29BFE 100%)',
                       color: 'white',
                       fontSize: '0.8rem'
                     }}>
                  <span className="me-1 me-sm-2" style={{ fontSize: '0.9rem' }}>🎯</span>
                  <span className="fw-semibold">Your Journey to Perfect Content</span>
                </div>
              </div>
              
              {/* Steps Container - Desktop/Large Screen */}
              <div className="d-none d-md-flex justify-content-between align-items-center position-relative mb-3" style={{ minWidth: 'fit-content' }}>
                {roadmapSteps.map((step, index) => (
                  <React.Fragment key={step.id}>
                    {/* Step Circle */}
                    <div className="text-center position-relative" style={{ zIndex: 2, flex: '0 0 auto' }}>
                      <div
                        className="rounded-circle d-flex align-items-center justify-content-center mx-auto mb-2 mb-lg-3 transition-all"
                        style={{
                          width: '70px',
                          height: '70px',
                          fontSize: '1.3rem',
                          fontWeight: 'bold',
                          transition: 'all 0.3s ease',
                          cursor: step.isActive ? 'pointer' : 'default',
                          ...getStepStyles(step)
                        }}
                        onClick={() => step.isActive && step.link !== '#' && openInNewTab(step.link)}
                      >
                        {step.isCompleted ? '✓' : step.id}
                      </div>
                      <div style={{ maxWidth: '100px', minWidth: '80px' }}>
                        <h6 className="fw-bold mb-1" style={{ 
                          color: step.isActive ? '#2c3e50' : '#6c757d',
                          fontSize: '0.9rem'
                        }}>
                          {step.title}
                        </h6>
                        <p className="small text-muted mb-0" style={{ 
                          fontSize: '0.75rem',
                          lineHeight: '1.2'
                        }}>
                          {step.subtitle}
                        </p>
                      </div>
                    </div>

                    {/* Connector Line */}
                    {index < roadmapSteps.length - 1 && (
                      <div 
                        className="flex-grow-1 mx-2"
                        style={{
                          ...getConnectorStyles(index),
                          borderRadius: '2px',
                          transition: 'all 0.3s ease',
                          position: 'relative',
                          top: '-35px'
                        }}
                      />
                    )}
                  </React.Fragment>
                ))}
              </div>

              {/* Mobile/Tablet Steps - Vertical Layout */}
              <div className="d-md-none">
                {roadmapSteps.map((step, index) => (
                  <div key={step.id} className="position-relative">
                    <div className="d-flex align-items-start mb-3 mb-sm-4">
                      <div
                        className="rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0"
                        style={{
                          width: '50px',
                          height: '50px',
                          fontSize: '1.1rem',
                          fontWeight: 'bold',
                          cursor: step.isActive ? 'pointer' : 'default',
                          ...getStepStyles(step)
                        }}
                        onClick={() => step.isActive && step.link !== '#' && openInNewTab(step.link)}
                      >
                        {step.isCompleted ? '✓' : step.id}
                      </div>
                      <div className="flex-grow-1 pt-1">
                        <h6 className="fw-bold mb-1" style={{ 
                          color: step.isActive ? '#2c3e50' : '#6c757d',
                          fontSize: '0.9rem'
                        }}>
                          {step.title}
                        </h6>
                        <p className="text-muted mb-0" style={{ 
                          fontSize: '0.75rem',
                          lineHeight: '1.3'
                        }}>
                          {step.subtitle}
                        </p>
                        {step.description && (
                          <p className="text-muted mb-0 mt-1 d-none d-sm-block" style={{ 
                            fontSize: '0.7rem',
                            lineHeight: '1.2'
                          }}>
                            {step.description}
                          </p>
                        )}
                      </div>
                    </div>
                    {/* Vertical Connector Line */}
                    {index < roadmapSteps.length - 1 && (
                      <div 
                        className="position-absolute"
                        style={{
                          left: '25px',
                          top: '50px',
                          width: '2px',
                          height: '25px',
                          ...getConnectorStyles(index)
                        }}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Progress Bar */}
            <div className="mb-4">
              <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center mb-2 gap-1">
                <small className="text-muted">Overall Progress</small>
                <small className="text-muted">{completedCount} of 4 steps completed</small>
              </div>
              <div 
                className="progress"
                style={{ height: '8px', borderRadius: '4px' }}
              >
                <div
                  className="progress-bar"
                  role="progressbar"
                  style={{
                    width: `${progressPercentage}%`,
                    background: progressPercentage === 100 
                      ? 'linear-gradient(90deg, #28a745 0%, #20c997 100%)'
                      : 'linear-gradient(90deg, #4A90E2 0%, #7CB342 100%)',
                    borderRadius: '4px',
                    transition: 'width 0.5s ease'
                  }}
                  aria-valuenow={progressPercentage}
                  aria-valuemin="0"
                  aria-valuemax="100"
                />
              </div>
            </div>

      {/* Global Note before Step 1 */}
      <div className="row mt-3">
        <div className="col-lg-9 col-md-10 col-sm-10">
          <NoteCard
            topic={"Note"}
            description={
              "All content headlines, outline and draft will be available to view on the history section of the tool. "
            }
            image={"/icon/help.svg"}
          />
        </div>
      </div>

            {/* Step 1: Headline Creation - Always visible */}
            <div className="mb-4">
              <div className={`card ${completedSteps.step1 ? 'border-success' : 'border-primary'}`}>
                <div className={`card-header ${completedSteps.step1 ? 'bg-success' : 'bg-primary'} text-white`}>
                  <h5 className="mb-0">
                    {completedSteps.step1 ? '✓' : '1'} Step 1: Headline Creation
                  </h5>
                </div>
                <div className="card-body">
                  <Form.Group>
                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <Form.Label>Keyword</Form.Label>
                        <Form.Control
                          type="text"
                          name="keyword"
                          placeholder="Enter keyword"
                          value={step1Data.keyword}
                          onChange={handleStep1InputChange}
                          onBlur={handleStep1Blur}
                          isInvalid={!!step1Data.errors.keyword}
                          required
                          disabled={completedSteps.step1 || step1Data.isLoading}
                          style={getInputFieldStyle(completedSteps.step1)}
                        />
                        <Form.Control.Feedback type="invalid">
                          {step1Data.errors.keyword}
                        </Form.Control.Feedback>
                      </div>
                      <div className="col-md-6 mb-3">
                        <Form.Label>Brand Name (Optional)</Form.Label>
                        <Form.Control
                          type="text"
                          name="brandName"
                          placeholder="Enter brand name"
                          value={step1Data.brandName}
                          onChange={handleStep1InputChange}
                          disabled={completedSteps.step1 || step1Data.isLoading}
                          style={getInputFieldStyle(completedSteps.step1)}
                        />
                      </div>
                      <div className="col-12 mb-3">
                        <div className="d-flex flex-column flex-sm-row gap-2">
                          {!completedSteps.step1 ? (
                            <>
                              <Button
                                variant="primary"
                                onClick={handleStep1Submit}
                                disabled={!step1Data.keyword.trim() || step1Data.isLoading}
                                className="flex-grow-1 flex-sm-grow-0"
                              >
                                {step1Data.isLoading ? (
                                  <>
                                    <span className="spinner-border spinner-border-sm me-2" />
                                    Generating...
                                  </>
                                ) : (
                                  "Generate Headlines"
                                )}
                              </Button>
                              <Button
                                variant="outline-secondary"
                                onClick={() => {
                                  setStep1Data({
                                    keyword: "",
                                    brandName: "",
                                    headlines: null,
                                    isLoading: false,
                                    errors: { keyword: "", brandName: "" }
                                  });
                                }}
                                disabled={step1Data.isLoading}
                                className="flex-grow-1 flex-sm-grow-0"
                              >
                                Reset Form
                              </Button>
                            </>
                          ) : (
                            <Button
                              variant="outline-success"
                              onClick={() => setCompletedSteps(prev => ({ ...prev, step1: false }))}
                              disabled={isAnyStepLoading}
                              className="flex-grow-1 flex-sm-grow-0"
                            >
                              {step1Data.isLoading ? (
                                <>
                                  <span className="spinner-border spinner-border-sm me-2" />
                                  Loading...
                                </>
                              ) : (
                                "Edit Step 1"
                              )}
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </Form.Group>
                  
                  {step1Data.headlines && (
                    <div className="mt-4">
                      <h6>Generated Headlines:</h6>
                      <TopTabBar
                        tabs={Object.keys(step1Data.headlines).map((key, index) => ({
                          label: `Draft ${String.fromCharCode(65 + index)}`,
                          element: (
                            <ArticleHeadlinesKeywordTable
                              data={step1Data.headlines[key]}
                              topic="Headlines"
                            />
                          ),
                        }))}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Step 2: Content Outline - Shows only after Step 1 is completed */}
            {completedSteps.step1 && (
              <div className="mb-4">
                <div className={`card ${completedSteps.step2 ? 'border-success' : 'border-primary'}`}>
                  <div className={`card-header ${completedSteps.step2 ? 'bg-success' : 'bg-primary'} text-white`}>
                    <h5 className="mb-0">
                      {completedSteps.step2 ? '✓' : '2'} Step 2: Content Outline
                    </h5>
                  </div>
                  <div className="card-body">
                    {step2Data.primaryKeyword && (
                      <div className="alert alert-info py-2 mb-3" style={{ fontSize: '0.85rem' }}>
                        <i className="fas fa-info-circle me-2"></i>
                        <strong>Primary Keyword:</strong> {step2Data.primaryKeyword} 
                        <small className="text-muted ms-2">(from Step 1)</small>
                      </div>
                    )}
                    <Form.Group>
                      <div className="row g-2 g-md-3">
                        <div className="col-12 col-lg-8 mb-2 mb-md-3">
                          <Form.Label className="small fw-semibold">Article Headline</Form.Label>
                          <Form.Control
                            type="text"
                            name="title"
                            placeholder="Enter title"
                            value={step2Data.title}
                            onChange={handleStep2InputChange}
                            onBlur={handleStep2Blur}
                            isInvalid={!!step2Data.errors.title}
                            required
                            disabled={completedSteps.step2 || step2Data.isLoading}
                            size="sm"
                            style={getInputFieldStyle(completedSteps.step2)}
                          />
                          <Form.Control.Feedback type="invalid">
                            {step2Data.errors.title}
                          </Form.Control.Feedback>
                        </div>
                        <div className="col-12 col-sm-6 col-lg-4 mb-2 mb-md-3">
                          <Form.Label className="small fw-semibold">Brand</Form.Label>
                          <Form.Control
                            type="text"
                            name="brand"
                            placeholder="Enter brand name"
                            value={step2Data.brand}
                            onChange={handleStep2InputChange}
                            onBlur={handleStep2Blur}
                            isInvalid={!!step2Data.errors.brand}
                            required
                            disabled={completedSteps.step2 || step2Data.isLoading}
                            size="sm"
                            style={getInputFieldStyle(completedSteps.step2)}
                          />
                          <Form.Control.Feedback type="invalid">
                            {step2Data.errors.brand}
                          </Form.Control.Feedback>
                        </div>
                        <div className="col-12 col-sm-6 col-lg-4 mb-2 mb-md-3">
                          <Form.Label className="small fw-semibold">Writer Model</Form.Label>
                          <Form.Select
                            name="model"
                            value={step2Data.model}
                            onChange={handleStep2InputChange}
                            onBlur={handleStep2Blur}
                            isInvalid={!!step2Data.errors.model}
                            required
                            disabled={completedSteps.step2 || step2Data.isLoading}
                            size="sm"
                            style={getInputFieldStyle(completedSteps.step2)}
                          >
                            <option value="">Select Writer Model</option>
                            {modelOptions.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </Form.Select>
                          <Form.Control.Feedback type="invalid">
                            {step2Data.errors.model}
                          </Form.Control.Feedback>
                        </div>
                        <div className="col-12 col-sm-6 col-lg-4 mb-2 mb-md-3">
                          <Form.Label className="small fw-semibold">Word Count</Form.Label>
                          <Form.Control
                            type="number"
                            name="wordCount"
                            placeholder="Enter word count"
                            value={step2Data.wordCount}
                            onChange={handleStep2InputChange}
                            onBlur={handleStep2Blur}
                            isInvalid={!!step2Data.errors.wordCount}
                            min="100"
                            max="1200"
                            disabled={completedSteps.step2 || step2Data.isLoading}
                            size="sm"
                            style={getInputFieldStyle(completedSteps.step2)}
                          />
                          <Form.Control.Feedback type="invalid">
                            {step2Data.errors.wordCount}
                          </Form.Control.Feedback>
                        </div>
                        <div className="col-12 mb-2 mb-md-3">
                          <div className="d-flex flex-column flex-sm-row gap-2 justify-content-start">
                            {!completedSteps.step2 ? (
                              <>
                                <Button
                                  variant="primary"
                                  onClick={handleStep2Submit}
                                  disabled={!step2Data.title.trim() || !step2Data.primaryKeyword.trim() || !step2Data.brand.trim() || !step2Data.model || step2Data.isLoading}
                                  className="btn-sm"
                                  style={{ minWidth: '140px' }}
                                >
                                  {step2Data.isLoading ? (
                                    <>
                                      <span className="spinner-border spinner-border-sm me-2" />
                                      Generating...
                                    </>
                                  ) : (
                                    "Generate Outline"
                                  )}
                                </Button>
                                <Button
                                  variant="outline-secondary"
                                  onClick={() => {
                                    setStep2Data(prev => ({
                                      ...prev,
                                      title: "",
                                      brand: "",
                                      model: "",
                                      wordCount: 800,
                                      outline: "",
                                      errors: { title: "", primaryKeyword: "", brand: "", model: "", wordCount: "" }
                                      // Note: primaryKeyword is preserved from Step 1
                                    }));
                                  }}
                                  disabled={step2Data.isLoading}
                                  className="btn-sm"
                                  style={{ minWidth: '120px' }}
                                >
                                  Reset Form
                                </Button>
                              </>
                            ) : (
                              <Button
                                variant="outline-success"
                                onClick={() => setCompletedSteps(prev => ({ ...prev, step2: false }))}
                                disabled={isAnyStepLoading}
                                className="btn-sm"
                                style={{ minWidth: '120px' }}
                              >
                                {step2Data.isLoading ? (
                                  <>
                                    <span className="spinner-border spinner-border-sm me-2" />
                                    Loading...
                                  </>
                                ) : (
                                  "Edit Step 2"
                                )}
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    </Form.Group>
                    
                    {/* Note Card between Edit button and Generated Outline - Show BEFORE getting response */}
                    {!completedSteps.step2 && (
                      <div className="row mt-3">
                        <div className="col-lg-9 col-md-10 col-sm-10">
                          <NoteCard
                            topic={"Note"}
                            description={
                              "Content outline will be generated within 5-7 mins, followed by the content draft"
                            }
                            image={"/icon/help.svg"}
                          />
                        </div>
                      </div>
                    )}
                    
                    {step2Data.outline && (
                      <div className="mt-4">
                        <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center mb-2 gap-2">
                          <h6 className="mb-0 small fw-bold">Generated Outline:</h6>
                          <Button
                            variant={copiedContent === 'outline' ? 'success' : 'outline-primary'}
                            size="sm"
                            onClick={() => handleCopyContent(step2Data.outline, 'outline')}
                            className="d-flex align-items-center btn-sm"
                            style={{ minWidth: '80px', fontSize: '0.75rem' }}
                          >
                            <i className={`fas ${copiedContent === 'outline' ? 'fa-check' : 'fa-copy'} me-1`} style={{ fontSize: '0.7rem' }}></i>
                            {copiedContent === 'outline' ? 'Copied!' : 'Copy'}
                          </Button>
                        </div>
                        <MarkdownRender variant="content">
                          {step2Data.outline}
                        </MarkdownRender>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}


            {/* Step 3: Content Draft - Shows only after Step 2 is completed */}
            {completedSteps.step2 && (
              <div className="mb-4">
                <div className={`card ${completedSteps.step3 ? 'border-success' : 'border-primary'}`}>
                  <div className={`card-header ${completedSteps.step3 ? 'bg-success' : 'bg-primary'} text-white`}>
                    <h5 className="mb-0">
                      {completedSteps.step3 ? '✓' : '3'} Step 3: Content Draft
                    </h5>
                  </div>
                  <div className="card-body">
                    <p>Using the outline from Step 2 to generate your content draft...</p>
                    {!completedSteps.step3 ? (
                      <div className="d-flex flex-column gap-2">
                        <div className="d-flex flex-column flex-sm-row gap-2">
                          <Button
                            variant="success"
                            onClick={() => handleStep3Submit("writer_1")}
                            disabled={step3Data.isLoading}
                            className="flex-grow-1 flex-sm-grow-0"
                          >
                            {step3Data.isLoading && step3Data.loadingModel === "writer_1" ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-2" />
                                Generating...
                              </>
                            ) : (
                              "Generate Content Draft"
                            )}
                          </Button>
                          <Button
                            variant="outline-success"
                            onClick={() => handleStep3Submit("writer_2")}
                            disabled={step3Data.isLoading}
                            className="flex-grow-1 flex-sm-grow-0"
                          >
                            {step3Data.isLoading && step3Data.loadingModel === "writer_2" ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-2" />
                                Generating...
                              </>
                            ) : (
                              "Generate Draft - Advanced"
                            )}
                          </Button>
                        </div>
                        <small className="text-muted d-block text-center">
                          This will use the content outline and primary keyword to generate a full draft
                        </small>
                      </div>
                    ) : (
                      <Button
                        variant="outline-success"
                        onClick={() => setCompletedSteps(prev => ({ ...prev, step3: false }))}
                        disabled={isAnyStepLoading}
                        className="flex-grow-1 flex-sm-grow-0"
                      >
                        {step3Data.isLoading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" />
                            Loading...
                          </>
                        ) : (
                          "Edit Step 3"
                        )}
                      </Button>
                    )}
                    
                    {/* Note Card between Edit button and Generated Draft - Show BEFORE getting response */}
                    {!completedSteps.step3 && (
                      <div className="row mt-3">
                        <div className="col-lg-9 col-md-10 col-sm-10">
                          <NoteCard
                            topic={"Note"}
                            description={
                              "Content outline will be generated within 5-7 mins, followed by the content draft"
                            }
                            image={"/icon/help.svg"}
                          />
                        </div>
                      </div>
                    )}
                    
                    {step3Data.draft && (
                      <div className="mt-4">
                        <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center mb-2 gap-2">
                          <h6 className="mb-0 small fw-bold">Generated Draft:</h6>
                          <Button
                            variant={copiedContent === 'draft' ? 'success' : 'outline-primary'}
                            size="sm"
                            onClick={() => handleCopyContent(step3Data.draft, 'draft')}
                            className="d-flex align-items-center btn-sm"
                            style={{ minWidth: '80px', fontSize: '0.75rem' }}
                          >
                            <i className={`fas ${copiedContent === 'draft' ? 'fa-check' : 'fa-copy'} me-1`} style={{ fontSize: '0.7rem' }}></i>
                            {copiedContent === 'draft' ? 'Copied!' : 'Copy'}
                          </Button>
                        </div>
                        <MarkdownRender variant="content">
                          {step3Data.draft}
                        </MarkdownRender>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}


            {/* Step 4: AI Overview - Shows only after Step 3 is completed */}
            {completedSteps.step3 && (
              <div className="mb-4">
                <div className={`card ${completedSteps.step4 ? 'border-success' : 'border-primary'}`}>
                  <div className={`card-header ${completedSteps.step4 ? 'bg-success' : 'bg-primary'} text-white`}>
                    <h5 className="mb-0">
                      {completedSteps.step4 ? '✓' : '4'} Step 4: AI Overview
                    </h5>
                  </div>
                  <div className="card-body">
                    <p className="text-muted mb-3" style={{ fontSize: '0.9rem' }}>
                      Using the content draft from Step 3 to generate AI overview analysis...
                    </p>
                      <Form.Group>
                        <div className="row g-2 align-items-end">
                          <div className="col-12 col-md-4 mb-2 mb-md-0">
                            <Form.Label className="small fw-semibold">Number of Queries (1-50)</Form.Label>
                            <Form.Control
                              type="number"
                              name="noQueries"
                              placeholder="Enter number of queries"
                              value={step4Data.noQueries}
                              onChange={handleStep4InputChange}
                              onBlur={handleStep4Blur}
                              isInvalid={!!step4Data.errors.noQueries}
                              min="1"
                              max="50"
                              disabled={completedSteps.step4 || step4Data.isLoading}
                              size="sm"
                              style={getInputFieldStyle(completedSteps.step4)}
                            />
                            <Form.Control.Feedback type="invalid">
                              {step4Data.errors.noQueries}
                            </Form.Control.Feedback>
                          </div>
                          <div className="col-12 col-md-4 mb-2 mb-md-0">
                            {!completedSteps.step4 ? (
                              <Button
                                variant="primary"
                                onClick={handleStep4Submit}
                                disabled={!step4Data.text.trim() || step4Data.isLoading}
                                className="w-100"
                                size="sm"
                              >
                                {step4Data.isLoading ? (
                                  <>
                                    <span className="spinner-border spinner-border-sm me-2" />
                                    Analyzing...
                                  </>
                                ) : (
                                  "Generate AI Overview"
                                )}
                              </Button>
                            ) : (
                              <Button
                                variant="outline-success"
                                onClick={() => setCompletedSteps(prev => ({ ...prev, step4: false }))}
                                disabled={isAnyStepLoading}
                                className="w-100"
                                size="sm"
                              >
                                {step4Data.isLoading ? (
                                  <>
                                    <span className="spinner-border spinner-border-sm me-2" />
                                    Loading...
                                  </>
                                ) : (
                                  "Edit Step 4"
                                )}
                              </Button>
                            )}
                          </div>
                          <div className="col-12 col-md-4 mb-2 mb-md-0">
                            {!completedSteps.step4 && (
                              <Button
                                variant="outline-secondary"
                                onClick={() => {
                                  setStep4Data({
                                    text: step4Data.text, // Preserve text from Step 3
                                    noQueries: 10,
                                    aiOverview: [],
                                    isLoading: false,
                                    errors: { noQueries: "" }
                                  });
                                }}
                                disabled={step4Data.isLoading}
                                className="w-100"
                                size="sm"
                              >
                                Reset Form
                              </Button>
                            )}
                          </div>
                        </div>
                      </Form.Group>
                    
                    {step4Data.aiOverview && step4Data.aiOverview.length > 0 && (
                      <div className="mt-4">
                        <h6>AI Overview Results:</h6>
                        <AiOverviewTable 
                          result={step4Data.aiOverview} 
                          onCopy={(message) => {
                            setShowSuccessNotification(true);
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}


            {/* Completion Message */}
            {progressPercentage === 100 && (
              <div className="mt-4">
                <div className="alert alert-success d-flex align-items-center" role="alert">
                  <span className="me-2" style={{ fontSize: '1.5rem' }}>🎉</span>
                  <div>
                    <strong>Congratulations!</strong> You've completed all steps in the content creation roadmap. Your content is ready to publish!
                  </div>
                </div>
              </div>
            )}

            {/* Reset Progress */}
            <div className="mt-4 text-center">
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => {
                  setCompletedSteps({
                    step1: false,
                    step2: false,
                    step3: false,
                    step4: false
                  });
                  // Reset all step data as well
                  setStep1Data({
                    keyword: "",
                    brandName: "",
                    headlines: null,
                    isLoading: false,
                    errors: { keyword: "", brandName: "" }
                  });
                  setStep2Data({
                    title: "",
                    primaryKeyword: "",
                    brand: "",
                    model: "",
                    wordCount: 800,
                    outline: "",
                    isLoading: false,
                    errors: { title: "", primaryKeyword: "", brand: "", model: "", wordCount: "" }
                  });
                  setStep3Data({
                    draft: "",
                    isLoading: false,
                    loadingModel: null,
                    errors: {}
                  });
                  setStep4Data({
                    text: "",
                    noQueries: 10,
                    aiOverview: [],
                    isLoading: false,
                    errors: { noQueries: "" }
                  });
                }}
              >
                Reset Progress
              </Button>
              </div>
            </div>
          </div>
      </div>
      {/* Notification Components */}
      <ToastAlert
        show={showSuccessNotification}
        message={copiedContent ? `${copiedContent.charAt(0).toUpperCase() + copiedContent.slice(1)} copied to clipboard!` : "Step completed successfully!"}
        type="success"
        icon="fas fa-check-circle"
        duration={3000}
        onClose={() => setShowSuccessNotification(false)}
        position="top-right"
      />

      <ToastAlert
        show={showErrorNotification}
        message={errorMessage}
        type="error"
        icon="fas fa-exclamation-circle"
        duration={5000}
        onClose={() => setShowErrorNotification(false)}
        position="top-right"
      />

      <InsufficientCreditsPopup
        show={showInsufficientCredits}
        onClose={closeInsufficientCredits}
        data={insufficientCreditsData}
      />
    </div>
  );
}
